import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import type {
  TriggerRule,
  CreateTriggerRuleRequest,
  UpdateTriggerRuleRequest,
  RepositorySettings,
  CreateRepositorySettingsRequest,
  UpdateRepositorySettingsRequest,
  AgentRun,
  PrGroup,
} from './types';

/** Optional parameters for the PR-group list (EMA-457). */
export interface PrGroupListParams {
  limit?: number;
  /**
   * Skip this many groups. The backend orders groups by last activity, so the key
   * moves as runs progress and pages are neither disjoint nor exhaustive: callers
   * walking offsets must de-duplicate by `groupId`, and must expect a group that
   * gains activity mid-walk to move up into an already-read page and so be missed
   * until the next walk.
   */
  offset?: number;
}

export interface AgentLaunchPlatformApi {
  getTriggerRules(): Promise<TriggerRule[]>;
  createTriggerRule(request: CreateTriggerRuleRequest): Promise<TriggerRule>;
  updateTriggerRule(
    id: number,
    request: UpdateTriggerRuleRequest,
  ): Promise<TriggerRule>;
  deleteTriggerRule(id: number): Promise<void>;
  /**
   * Lists repository settings, optionally narrowed to one repository (`org/repo`),
   * which returns 0..1 items.
   *
   * `GET /api/repository-settings/{id}` is deliberately not exposed: the list
   * response already carries every field the UI renders, so a by-id fetch would
   * be dead code.
   */
  getRepositorySettings(repository?: string): Promise<RepositorySettings[]>;
  createRepositorySettings(
    request: CreateRepositorySettingsRequest,
  ): Promise<RepositorySettings>;
  updateRepositorySettings(
    id: number,
    request: UpdateRepositorySettingsRequest,
  ): Promise<RepositorySettings>;
  deleteRepositorySettings(id: number): Promise<void>;
  retryExecution(executionId: number): Promise<void>;
  cancelExecution(executionId: number): Promise<void>;
  getAgentRun(agentRunId: string): Promise<AgentRun>;
  getPrGroups(params?: PrGroupListParams): Promise<PrGroup[]>;
  getPrGroup(groupId: number): Promise<PrGroup>;
}

// The id predates the NFS migration and is kept as is. On its own it would make
// the frontend system infer `agent-launch-platform` as the owning plugin, which
// is not this plugin's id, so the owner is given explicitly.
export const agentLaunchPlatformApiRef =
  createApiRef<AgentLaunchPlatformApi>().with({
    id: 'plugin.agent-launch-platform.service',
    pluginId: 'arrive-agent-launch-platform',
  });

async function throwOnError(
  response: Response,
  fallback: string,
): Promise<void> {
  if (response.ok) return;
  let detail = '';
  try {
    const body = await response.text();
    if (body) detail = `: ${body}`;
  } catch {
    // ignore read errors
  }
  throw new Error(`${fallback} (HTTP ${response.status}${detail})`);
}

export class AgentLaunchPlatformClient implements AgentLaunchPlatformApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl(): Promise<string> {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    return `${proxyUrl}/agent-launch-platform`;
  }

  async getTriggerRules(): Promise<TriggerRule[]> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${base}/api/trigger-rules`);
    await throwOnError(response, 'Failed to fetch trigger rules');
    return response.json();
  }

  async createTriggerRule(
    request: CreateTriggerRuleRequest,
  ): Promise<TriggerRule> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${base}/api/trigger-rules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    await throwOnError(response, 'Failed to create trigger rule');
    return response.json();
  }

  async updateTriggerRule(
    id: number,
    request: UpdateTriggerRuleRequest,
  ): Promise<TriggerRule> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${base}/api/trigger-rules/${id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
    await throwOnError(response, 'Failed to update trigger rule');
    return response.json();
  }

  async deleteTriggerRule(id: number): Promise<void> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${base}/api/trigger-rules/${id}`,
      { method: 'DELETE' },
    );
    await throwOnError(response, 'Failed to delete trigger rule');
  }

  async getRepositorySettings(
    repository?: string,
  ): Promise<RepositorySettings[]> {
    const base = await this.baseUrl();
    const query = new URLSearchParams();
    if (repository !== undefined) query.set('repository', repository);
    const qs = query.toString();
    const response = await this.fetchApi.fetch(
      `${base}/api/repository-settings${qs ? `?${qs}` : ''}`,
    );
    await throwOnError(response, 'Failed to fetch repository settings');
    return response.json();
  }

  async createRepositorySettings(
    request: CreateRepositorySettingsRequest,
  ): Promise<RepositorySettings> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${base}/api/repository-settings`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
    await throwOnError(response, 'Failed to create repository settings');
    return response.json();
  }

  async updateRepositorySettings(
    id: number,
    request: UpdateRepositorySettingsRequest,
  ): Promise<RepositorySettings> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${base}/api/repository-settings/${encodeURIComponent(String(id))}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
    await throwOnError(response, 'Failed to update repository settings');
    return response.json();
  }

  async deleteRepositorySettings(id: number): Promise<void> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${base}/api/repository-settings/${encodeURIComponent(String(id))}`,
      { method: 'DELETE' },
    );
    await throwOnError(response, 'Failed to delete repository settings');
  }

  async retryExecution(executionId: number): Promise<void> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${base}/api/trigger-executions/${executionId}/retry`,
      { method: 'POST' },
    );
    await throwOnError(response, 'Failed to retry execution');
  }

  async cancelExecution(executionId: number): Promise<void> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${base}/api/trigger-executions/${executionId}/cancel`,
      { method: 'POST' },
    );
    await throwOnError(response, 'Failed to cancel execution');
  }

  async getAgentRun(agentRunId: string): Promise<AgentRun> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${base}/api/agent-runs/${encodeURIComponent(agentRunId)}`,
    );
    await throwOnError(response, 'Failed to fetch agent run');
    return response.json();
  }

  async getPrGroups(params: PrGroupListParams = {}): Promise<PrGroup[]> {
    const base = await this.baseUrl();
    const query = new URLSearchParams();
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.offset !== undefined) query.set('offset', String(params.offset));
    const qs = query.toString();
    const response = await this.fetchApi.fetch(
      `${base}/api/pull-requests${qs ? `?${qs}` : ''}`,
    );
    await throwOnError(response, 'Failed to fetch pull-request groups');
    return response.json();
  }

  async getPrGroup(groupId: number): Promise<PrGroup> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${base}/api/pull-requests/${encodeURIComponent(String(groupId))}`,
    );
    await throwOnError(response, 'Failed to fetch pull-request group');
    return response.json();
  }
}
